/**
 * MMX Reviews — the review row.
 *
 * The same scroll track serves both artboards: three reviews sit side by side on desktop and
 * fill the row exactly, so nothing overflows and the controls stay hidden; on mobile one fills
 * the row at a time and the arrows and dots appear.
 */

const SCROLL_SETTLE = 80;
const OVERFLOW_SLACK = 2;

class MmxReviews extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-mmx-reviews-track]');
    if (!this.track) return;

    this.items = Array.from(this.track.querySelectorAll('[data-mmx-reviews-item]'));
    if (this.items.length === 0) return;

    this.controls = this.querySelector('[data-mmx-reviews-controls]');
    this.dotsEl = this.querySelector('[data-mmx-reviews-dots]');
    this.prevEl = this.querySelector('[data-mmx-reviews-prev]');
    this.nextEl = this.querySelector('[data-mmx-reviews-next]');

    this.#buildDots();
    this.prevEl?.addEventListener('click', () => this.#step(-1));
    this.nextEl?.addEventListener('click', () => this.#step(1));

    this.track.addEventListener('scroll', () => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.#sync(), SCROLL_SETTLE);
    });

    this.#sync();
    this.resizeObserver = new ResizeObserver(() => this.#sync());
    this.resizeObserver.observe(this.track);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.resizeObserver?.disconnect();
  }

  get #index() {
    const centre = this.track.scrollLeft + this.track.clientWidth / 2;
    let best = 0;
    let bestDistance = Infinity;
    this.items.forEach((item, i) => {
      const distance = Math.abs(item.offsetLeft + item.offsetWidth / 2 - centre);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    });
    return best;
  }

  #step(direction) {
    this.#goTo(this.#index + direction);
  }

  #goTo(index) {
    const item = this.items[Math.min(Math.max(index, 0), this.items.length - 1)];
    if (!item) return;
    this.track.scrollTo({
      left: item.offsetLeft - (this.track.clientWidth - item.offsetWidth) / 2,
      behavior: 'smooth',
    });
  }

  #buildDots() {
    if (!this.dotsEl) return;

    this.dots = this.items.map((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'mmx-reviews__dot';
      dot.setAttribute('aria-label', `Go to review ${i + 1}`);
      dot.addEventListener('click', () => this.#goTo(i));
      this.dotsEl.appendChild(dot);
      return dot;
    });
  }

  /**
   * Three reviews fill the desktop row exactly, so the controls are driven off the actual
   * overflow rather than a breakpoint — add a fourth and they appear there too.
   */
  #sync() {
    if (this.controls) {
      const overflows = this.track.scrollWidth - this.track.clientWidth > OVERFLOW_SLACK;
      this.controls.hidden = !overflows || this.items.length < 2;
    }

    const active = this.#index;
    this.dots?.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === active);
      dot.setAttribute('aria-current', i === active ? 'true' : 'false');
    });

    if (this.prevEl) this.prevEl.disabled = active === 0;
    if (this.nextEl) this.nextEl.disabled = active === this.items.length - 1;
  }
}

if (!customElements.get('mmx-reviews')) {
  customElements.define('mmx-reviews', MmxReviews);
}
